import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { LimitWindowsField } from "./LimitWindowsField";
import {
  LimitWindow,
  RateLimitSetting,
  UNEDITABLE_VALUE_HINT,
  parseLimitValue,
  saveLooksReverted,
  serializeLimitWindows,
  sourceMeta,
  validateLimitWindows,
} from "./rateLimit";

interface PropsInterface {
  setting: RateLimitSetting;
  label: string;
  description?: string;
  /** Rendered next to the label — used for the "not enforced yet" notice. */
  badge?: React.ReactNode;
  /**
   * Appended to the revert warning when this row is known to be overridable by
   * keys the panel cannot read. See `REGISTRATION_OVERRIDE_HINT`.
   */
  overrideHint?: string;
  saving: boolean;
  onSave: (key: string, value: string) => Promise<boolean>;
}

export const RateLimitSettingRow: React.FC<PropsInterface> = ({
  setting,
  label,
  description,
  badge,
  overrideHint,
  saving,
  onSave,
}) => {
  const [windows, setWindows] = useState<Array<LimitWindow> | null>(() =>
    parseLimitValue(setting.value)
  );
  const [error, setError] = useState("");

  /**
   * The value this row is measured against for "unsaved changes".
   *
   * After a save this becomes what we sent, NOT what the server reports: the
   * server keeps serving the old value for up to 30 seconds while instances
   * refresh their caches, so comparing against `setting.value` would leave the
   * row looking permanently dirty and imply the save had failed.
   */
  const [savedValue, setSavedValue] = useState<string | null>(null);
  /** When that write went out, so a stuck value can be told from a slow one. */
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Re-seed when the server value actually changes, i.e. after an explicit
  // refresh that picked up new data.
  useEffect(() => {
    setWindows(parseLimitValue(setting.value));
    setSavedValue(null);
    setSavedAt(null);
    setError("");
  }, [setting.value]);

  const meta = sourceMeta(setting.source);
  const baseline = savedValue ?? setting.value;
  const serialized = windows ? serializeLimitWindows(windows) : "";
  const dirty = Boolean(windows) && serialized !== baseline;
  /**
   * A write the server has now contradicted for longer than its own cache
   * window explains. Evaluated during render so the first refresh past the
   * grace period surfaces it — nothing on this screen polls.
   */
  const reverted = saveLooksReverted(savedValue, setting.value, savedAt);
  const pending = savedValue !== null && savedValue !== setting.value && !reverted;

  const handleSave = async () => {
    if (!windows) return;

    const validationError = validateLimitWindows(windows);

    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");

    const value = serializeLimitWindows(windows);
    const saved = await onSave(setting.key, value);

    if (saved) {
      setSavedValue(value);
      setSavedAt(Date.now());
    }
  };

  return (
    <Box sx={{ py: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        rowGap={1}
        sx={{ mb: 0.5 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {label}
        </Typography>

        <Tooltip title={meta.help}>
          <Chip size="small" label={meta.label} color={meta.color} />
        </Tooltip>

        {badge}

        {pending && (
          <Chip
            size="small"
            variant="outlined"
            color="success"
            label="Saved — applies within 30 seconds"
          />
        )}
      </Stack>

      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {description}
        </Typography>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5 }}
      >
        <code>{setting.key}</code>
      </Typography>

      {reverted && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <AlertTitle>This change did not take effect</AlertTitle>
          <Typography variant="body2" sx={{ mb: overrideHint ? 1 : 0 }}>
            You saved <code>{savedValue}</code>, but the server is still
            reporting <code>{setting.value}</code> well after the 30-second
            propagation window. Something is overriding this row.
          </Typography>
          {overrideHint}
        </Alert>
      )}

      {windows ? (
        <>
          <LimitWindowsField
            windows={windows}
            onChange={setWindows}
            disabled={saving}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {error}
            </Alert>
          )}

          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5 }}>
            <Button
              variant="contained"
              size="small"
              disabled={!dirty || saving}
              onClick={handleSave}
            >
              {saving ? "Saving…" : "Save"}
            </Button>

            {dirty && (
              <Typography variant="caption" color="text.secondary">
                Unsaved changes — server currently has{" "}
                <code>{setting.value}</code>
              </Typography>
            )}
          </Stack>
        </>
      ) : (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ mb: 1 }}>
            Current value: <code>{setting.value}</code>
          </Typography>
          {UNEDITABLE_VALUE_HINT}
        </Alert>
      )}
    </Box>
  );
};
